import { useEffect, useRef, useState } from 'react';
import { apiClient } from '@tark/shared/lib';
export type LeagueDeskTeam = {
  teamPublicId: string;
  teamName: string;
  status: string;
  waitlistPosition: number | null;
  primaryName: string;
  partnerName: string | null;
};
const fields = ['teamPublicId', 'teamName', 'status', 'waitlistPosition', 'primaryName', 'partnerName'];
function teamRows(value: unknown): LeagueDeskTeam[] {
  if (!Array.isArray(value) || value.some(row => !row || typeof row !== 'object' || Object.keys(row).sort().join() !== [...fields].sort().join() || typeof row.teamPublicId !== 'string' || typeof row.teamName !== 'string' || typeof row.status !== 'string' || typeof row.primaryName !== 'string' || row.partnerName !== null && typeof row.partnerName !== 'string' || row.waitlistPosition !== null && (!Number.isInteger(row.waitlistPosition) || row.waitlistPosition < 1))) throw new Error('Invalid registrations response');
  return value as LeagueDeskTeam[];
}
function actionError(error: unknown) {
  const data = (error as {
    response?: {
      data?: {
        error?: string;
        message?: string;
      };
    };
  })?.response?.data;
  if (data?.error === 'LEAGUE_WITHDRAW_REASON_REQUIRED') return 'Enter a withdrawal reason (up to 500 characters).';
  if (data?.error === 'COMPETITION_ORGANIZER_ACCESS_REQUIRED' || data?.error === 'LEAGUE_TEAM_ACCESS_DENIED') return 'Only the event owner or an active organizer admin can withdraw this team.';
  if (data?.error === 'LEAGUE_EVENT_CLOSED') return 'This event is closed; the team cannot be withdrawn.';
  return 'Could not withdraw the team. Reload registrations before trying again.';
}
const labels: Record<string, string> = {
  PENDING: 'Partner acceptance pending',
  CONFIRMED: 'Confirmed',
  WAITLISTED: 'Waitlisted',
  WITHDRAWN: 'Withdrawn',
  DROPPED: 'Registration incomplete'
};
export function LeagueRegistrationsPanel({
  eventId
}: {
  eventId: string;
}) {
  const [teams, setTeams] = useState<LeagueDeskTeam[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(''),
    [success, setSuccess] = useState(''),
    [busy, setBusy] = useState(false),
    [selected, setSelected] = useState<LeagueDeskTeam | null>(null),
    [reason, setReason] = useState('');
  const pending = useRef(false),
    reasonInput = useRef<HTMLTextAreaElement>(null),
    successMessage = useRef<HTMLParagraphElement>(null),
    returnFocus = useRef<HTMLButtonElement | null>(null);
  const path = `/api/v1/web/organizer/leagues/${encodeURIComponent(eventId)}/teams`;
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    void apiClient.get(path).then(result => {
      const rows = teamRows(result.data);
      if (current) setTeams(rows);
    }).catch(() => {
      if (current) setError('Could not load registrations. Reopen this event to retry.');
    }).finally(() => {
      if (current) setLoading(false);
    });
    return () => {
      current = false;
    };
  }, [path]);
  useEffect(() => {
    if (selected) reasonInput.current?.focus();
  }, [selected]);
  useEffect(() => {
    if (success) successMessage.current?.focus();
  }, [success]);
  const close = () => {
    setSelected(null);
    returnFocus.current?.focus();
  };
  const withdraw = async () => {
    if (pending.current || !selected || !reason.trim() || reason.length > 500) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.post(`/api/v1/web/organizer/leagues/teams/${selected.teamPublicId}/withdraw`, {
        reason: reason.trim()
      });
      setTeams(teamRows((await apiClient.get(path)).data));
      setSuccess(`${selected.teamName} withdrawn. The waitlist is updated.`);
      close();
    } catch (error) {
      setError(actionError(error));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  const list = (rows: LeagueDeskTeam[]) => <ul className="space-y-3">{rows.map(team => <li key={team.teamPublicId} className="rounded-lg border border-border p-3 break-words"><p className="font-semibold">{team.teamName}</p><p>{team.primaryName}{team.partnerName ? ` / ${team.partnerName}` : ' · Partner acceptance pending'}</p><p>{labels[team.status] || 'Status unavailable'}{team.waitlistPosition ? ` · Waitlist ${team.waitlistPosition}` : ''}</p>{['PENDING', 'CONFIRMED', 'WAITLISTED'].includes(team.status) && <button className="mt-2 min-h-11 rounded border border-border px-3" disabled={busy} onClick={event => {
        returnFocus.current = event.currentTarget;
        setSelected(team);
        setReason('');
        setError('');
      }}>Withdraw {team.teamName}</button>}</li>)}</ul>;
  return <section aria-label="Team registrations" className="space-y-3"><h2 className="text-xl font-bold">Team registrations</h2>{error && <p role="alert">{error}</p>}{success && <p ref={successMessage} tabIndex={-1} role="status">{success}</p>}{loading ? <p role="status">Loading registrations…</p> : <>{list(teams.filter(t => t.status !== 'WAITLISTED'))}<h3 className="font-bold">Waitlist</h3>{list(teams.filter(t => t.status === 'WAITLISTED').sort((a, b) => (a.waitlistPosition || 0) - (b.waitlistPosition || 0)))}{!teams.length && <p>No teams registered yet.</p>}</>}
 {selected && <section role="dialog" aria-label={`Withdraw ${selected.teamName}`} className="rounded border border-border p-4 space-y-3"><h3 className="font-bold">Withdraw {selected.teamName}?</h3><p>Before the draw, a waiting team may be promoted. This reason is recorded in the organizer audit.</p><label className="block" htmlFor="league-withdraw-reason">Withdrawal reason</label><textarea ref={reasonInput} id="league-withdraw-reason" value={reason} maxLength={500} onChange={event => setReason(event.target.value)} className="block w-full min-h-24 border border-border p-2" /><div className="flex flex-wrap gap-3"><button disabled={busy} className="min-h-11 border border-border px-3" onClick={close}>Keep team</button><button disabled={busy || !reason.trim()} className="min-h-11 border border-border px-3" onClick={() => void withdraw()}>Confirm withdrawal</button></div></section>}
 </section>;
}
